/**
 * Firebase Functions Service
 * 
 * Provides an interface to Firebase Cloud Functions for the application.
 * This service manages all communication with backend cloud functions including:
 * - User management (claims, token creation)
 * - Account operations (creation, updates)
 * - Communication services (SMS, email)
 * - Utility functions (address conversion, backup management)
 * - Usage tracking and analytics
 * 
 * Uses the Firebase Functions modular API to interact with Firebase Cloud Functions.
 * Includes development support for local emulator when running in debug mode.
 */
/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { 
    Functions, 
    httpsCallable, 
    getFunctions, 
    connectFunctionsEmulator 
} from '@angular/fire/functions';
import { firstValueFrom } from 'rxjs';

import { AccountProfileService } from '../lib/accountProfile.service';
import { FirestorePathsService } from './firestorePaths.service';
import { EventsService } from './events.service';
import { FirestoreService } from '../lib/firestore.service';
import { SessionService } from '../lib/session.service';
import { UtilsService } from '../lib/utils.service';
import { UxHelperService } from '../lib/uxHelper.service';
import { Constants } from '@shared/models';
import type { TClaim, TCustomer, TSmsType, TCreateAccountInfo, TEmail, TAccountProfile } from '@shared/models';

type TEnvelope<T = unknown> = {
    ok: boolean;
    data: T | null;
    error: { code: string; message: string } | null;
    meta: { endpoint: string; timestamp: string };
};

@Injectable({
    'providedIn': 'root'
})
export class FirebaseFunctionsService {
    private readonly EMULATOR_PORT = 5001;
    private functions: Functions;

    constructor(
        private sessionService: SessionService, 
        private firestoreService: FirestoreService,
        private firestorePathsService: FirestorePathsService, 
        private uxHelperService: UxHelperService,
        private httpClient: HttpClient,
        private accountProfileService: AccountProfileService, 
        private eventsService: EventsService,
        private utilsService: UtilsService
    ) {
        // Initialize Firebase Functions
        this.functions = getFunctions();

        // Only connect to the Functions emulator when running Cypress tests.
        // During normal localhost development, calls go to production functions directly.
        const isCypressTest = (window as any).Cypress !== undefined;
        if (isCypressTest) {
            console.log('🧪 Cypress detected - connecting to Firebase Functions emulator on localhost:' + this.EMULATOR_PORT);
            connectFunctionsEmulator(this.functions, 'localhost', this.EMULATOR_PORT);
        }

        this.eventsService.subscribe(Constants.EVENT_PAGE_LOADED, async (data) => {
            await this.onTrackUsage(data);
        });
    }

    /**
     * Helper method to call Firebase functions with error handling
     * @param functionName The name of the Firebase function to call
     * @param data The data to pass to the function
     * @param defaultErrorMessage Optional custom error message to use if the function fails
     */
    private async callFunction(functionName: string, data: any = {}, defaultErrorMessage?: string): Promise<any> {
        try {
            const functionRef = httpsCallable(this.functions, functionName);
            const result = await functionRef(data);
            return result.data;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            console.error(`Error calling function ${functionName}:`, errorMessage, err);
            throw new Error(defaultErrorMessage ? `${defaultErrorMessage}: ${errorMessage}` : errorMessage);
        }
    }

    private unwrapEnvelope<T>(response: any, defaultErrorMessage: string): T {
        const envelope = response as TEnvelope<T>;

        if (!envelope?.ok) {
            const message = envelope?.error?.message || defaultErrorMessage;
            throw new Error(message);
        }

        return envelope.data as T;
    }

    public async chatbotHealth(): Promise<unknown> {
        const response = await this.callFunction(
            'chatbotHealthCall',
            {},
            'Chatbot health check failed'
        );

        return this.unwrapEnvelope(response, 'Chatbot health check failed');
    }

    public async chatbotAsk(question: string, debug = false): Promise<unknown> {
        if (!question || !question.trim()) {
            throw new Error('question is required');
        }

        const response = await this.callFunction(
            'chatbotAskCall',
            { question: question.trim(), debug },
            'Chatbot ask failed'
        );

        return this.unwrapEnvelope(response, 'Chatbot ask failed');
    }
    
    public async addAccountToTOC(accountNumber: string): Promise<unknown> {
        if (!accountNumber) {
            throw new Error('addAccountToTOC Error: Invalid parameters.');
        }

        const parms = { accountNumber };
        const response = await this.callFunction('addAccountToTOC', parms, 'Failed to add account to Table of Contents');
        return response;
    }

    public async convertStreetAddressToLatLng(streetAddress: string) {
        const parms = { streetAddress };
        const response = await this.callFunction('convertStreetAddressToLatLng', parms, 'Failed to convert street address to coordinates');
        return response;
    }

    public async testFirebaseFunction() {
        const response = await this.callFunction('test', {}, 'Test function failed');
        return response;
    }

    public async sendEmail(email: TEmail) {
        const response = await this.callFunction('sendEmail', email, 'Failed to send email');
        return response;
    }

    public async sendSms(customers: TCustomer[], smsType: TSmsType, textMessage: string): Promise<unknown> {
        if (!customers || customers.length === 0 || !textMessage) {
            throw new Error('sendSms Error: Invalid parameters.');
        }

        try {
            const accountProfile = await this.accountProfileService.get();
            if (accountProfile.packageName === 'Pay As You Go Package' && accountProfile.creditInfo.availableCredits < 1) {
                throw new Error('Message Not Sent - Insufficient Available Credits.');
            }

            const cellPhone = customers[0].cellPhone;
            const allCharsEqual = this.utilsService.allEqual(cellPhone);
            if (allCharsEqual) {
                throw new Error('Message Not Sent - Cell Phone Number Appears To Be Invalid: ' + cellPhone);
            }

            const accountNumber = this.sessionService.getAccountNumber();
            const countryCode = this.sessionService.getDealership().address.isoCountryCode;
            const parms = {
                accountNumber,
                customers,
                smsType,
                countryCode,
                textMessage
            };

            await this.uxHelperService.showToast('Sending message...');
            const response = await this.callFunction('sendSms', parms, 'Failed to send SMS message');
            await this.uxHelperService.showToast('The message was sent.');
            return response;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            console.error('Error', errorMessage);
            throw err;
        }
    }

    public async getCampaignMetadata(accountNumber: string, customerUid: string, incentiveSetId: string): Promise<unknown> {
        const parms = {
            accountNumber,
            customerUid,
            incentiveSetId
        };

        const response = await this.callFunction('getCampaign', parms, 'Failed to get campaign metadata');
        console.log('In firebaseFunctionsService.getCampaign() Success; response =', response);
        return Date.now();
    }

    public async updateExpirationDateCount(accountNumber: string) {
        const parms = { accountNumber };
        const response = await this.callFunction('expirationDateExtension', parms, 'Failed to update expiration date count');
        console.log('In firebaseFunctionsService.expirationDateExtension() Success; result =', response);
        return Date.now();
    }

    public async restoreFromBackup(backupFilename: string, accountNumber: string) {
        const parms = { 
            timestamp: backupFilename, 
            accountNumber 
        };
        
        const response = await this.callFunction('restoreBackup', parms, 'Failed to restore backup');
        return response;    //Date.now();
    }

    public async getCurrentId(): Promise<unknown> {
        const response = await this.callFunction('getCurrentId', {}, 'Failed to get current ID');
        return response;
    }

    public async getNextIncentiveSetId(): Promise<number> {
        const response = await this.callFunction('getNextIncentiveSetId', {}, 'Failed to get next incentive set ID');
        return response;
    }

    public async getNextCustomerId(): Promise<string> {
        const response = await this.callFunction('getNextCustomerId', {}, 'Failed to get next customer ID');
        return response as string;
    }

    public async onTrackUsage(pageName: string): Promise<void> {
        try {
            // Don't track usage counts if we are doing stuff on the backend.
            if (this.sessionService.getApp() === 'backend') {
                return;
            }
            
            // The only kpi's we are truly interested in are tickets, routes, navigation and campaigns.
            switch (pageName) {
                case Constants.VIEW_TICKETS_PAGE3:
                case Constants.VIEW_ROUTE_PAGE8:
                case Constants.VIEW_NAVIGATION_PAGE5:
                case Constants.VIEW_TRACKER_LIVE:
                case Constants.VIEW_TRACKER_HISTORICAL:
                case Constants.VIEW_REPORTS_PAGE3:
                case Constants.VIEW_CAMPAIGN_INCENTIVE_SETS:
                case Constants.VIEW_CAMPAIGNS:
                case Constants.VIEW_CUSTOMERS:
                    break;

                default:
                    return;
            }
            
            const accountNumber = this.sessionService.getAccountNumber();
            if (!accountNumber) {
                // If we don't have an accountNumber then all that means is the user hasn't logged in yet.
                return;
            }

            const parms = { accountNumber, pageName };
            const response = await this.callFunction('trackUsage', parms, 'Failed to track usage');
            return response;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            console.error('Error', errorMessage);
            throw err;
        }
    }

    public async setUserClaim(claim: TClaim): Promise<void> {
        if (!claim) {
            return;
        }

        const parms = {
            accountNumber: claim.accountNumber,
            role: claim.role,
            userUid: claim.userUid
        };

        const response = await this.callFunction('setUserClaim', parms, 'Failed to set user claim');
        return response;
    }

    public async createCustomToken(userUid: string) {
        if (!userUid) {
            return;
        }

        const parms = { userUid };
        const response = await this.callFunction('createCustomToken', parms, 'Failed to create custom token');
        return response;
    }

    public async createAccount(accountInfo: TCreateAccountInfo) {
        if (!accountInfo) {
            return;
        }

        const response = await this.callFunction('createAccount', accountInfo, 'Failed to create account');
        return response;
    }

    public async deleteUser(email: string) {
        if (!email) {
            return;
        }
        
        const parms = { email };
        const response = await this.callFunction('deleteUser', parms, 'Failed to delete user');
        return response;
    }

    public async disableUser(email: string) {
        if (!email) {
            return;
        }
        
        const parms = { email };
        const response = await this.callFunction('disableUser', parms, 'Failed to disable user');
        return response;
    }

    public async resetCypress(): Promise<unknown> {
        const response = await this.callFunction('resetCypress', {}, 'Failed to reset Cypress');
        return response;
    }

    // Called from loader service. 
    public async evaluateRewardFunnelRules(accountNumber: string): Promise<void> {
        if (!accountNumber || accountNumber.length === 0) {
            throw new Error('evaluateRewardFunnelRules Error: Invalid parameters.');
        }

        try {
            const parms = {
                accountNumber,
            };

            // await this.uxHelperService.showToast('Evaluating Reward Funnel Rules...');
            const response = await this.callFunction('evaluateRewardFunnelRules', parms, 'Failed to evaluate reward funnel rules');
            // await this.uxHelperService.showToast('The reward funnel rules were evaluated.');
            return response;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            console.error('EvaluateRewardFunnelRules Error', errorMessage);
            throw err;
        }
    }

    public async testRewardFunnel() {
        const parms = { functionToTest: 'rewardFunnel', accountNumber: '00000-0000-00000', categoryFilter: 'onboarding' };
        
        const response = await this.callFunction('test', parms, 'Test function failed');
        return response;
    }

    public async testCreateAccount() {
        const parms: TCreateAccountInfo = { 
            'user': { 
                'firstName': 'aa', 
                'lastName': 'bb', 
                'cellPhone': '6618778631', 
                'email': 'deleteme201@shuttlenomix.com', 
                'role': 'manager', 
                'uid': '' 
            }, 
            'questionsAndAnswers': { 
                'hasReliableInternet': false, 
                'locatedInUnitedStates': false, 
                'isServiceManager': false, 
                'acceptsTrialTerms': false, 
                'commitsToOnboarding': false 
            } 
        };
        const response = await this.callFunction('createAccount', parms, 'Test function failed');
        return response;
    }

    // public async getLiveTrackingMetaData(accountId: string): Promise<TAccountProfile> {
    //     if (!accountId) {
    //         throw new Error('getLiveTrackingMetaData Error: Invalid parameters.');
    //     }
    //     const parms = { accountId };
    //     const response = await this.callFunction('getLiveTrackingMetaData', parms, 'Failed to get live tracking metadata');
    //     return response;
    // }
}